import { A } from '@adornis/base/env-info';
import { logger } from '@adornis/base/logging';
import type { Maybe } from '@adornis/base/utilTypes';
import { Int } from '@adornis/baseql/baseqlTypes';
import { Arg, Entity, Field, Query } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity';
import { selectionSet, type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration';
import { Base } from '@adornis/buildify/db/Base.js';
import { getInstagramToken } from '@adornis/digitale-helden-shared/features/instagram/api/getInstagramToken';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { config } from '../../config';

const API_RESP_ATTR_KEYS = ['id', 'caption', 'media_type', 'media_url', 'permalink', 'thumbnail_url', 'timestamp'];

interface InstagramApiPostResponse {
  id: string;
  media_type: string;
  media_url: string;
  thumbnail_url: string;
  permalink: string;
  caption: string;
}
interface InstagramApiGalleryPaging {
  cursors: {
    after: string;
    before: string;
  };
  next: string;
}

interface InstagramApiGalleryResponse {
  data: InstagramApiPostResponse[];
  paging: InstagramApiGalleryPaging;
}

function getInstagramPostFromApiResponse(curPostResponse: InstagramApiPostResponse) {
  return new InstagramPost({
    _id: A.getGloballyUniqueID(),
    postID: curPostResponse.id,
    type: curPostResponse.media_type,
    mediaUrl: curPostResponse.media_url,
    thumbnailUrl: curPostResponse.thumbnail_url,
    postUrl: curPostResponse.permalink,
    description: curPostResponse.caption,
  });
}

function getInstagramGalleryFromApiResponse(curPostResponse: InstagramApiGalleryResponse) {
  const response = curPostResponse.data.map(curPostResponse =>
    getInstagramPostFromApiResponse(curPostResponse).toJSON(),
  );
  const pagResp = new InstagramGalleryPagination({
    _id: A.getGloballyUniqueID(),
    response,
    after: curPostResponse.paging.cursors.after,
    before: curPostResponse.paging.cursors.before,
    next: curPostResponse.paging.next,
  });

  return pagResp;
}

@Entity()
export class InstagramPost extends AdornisEntity {
  static override _class = 'InstagramPost';

  static defaultFields() {
    return selectionSet(() => InstagramPost, 1);
  }
  static override resolveID(entity: InstagramPost): string {
    if (!entity._id) return (entity._id = A.getGloballyUniqueID());
    return entity._id;
  }

  @validate(nonOptional())
  @Field(type => String)
  _id!: string;

  @validate(nonOptional())
  @Field(type => String)
  postID!: string;

  @validate(nonOptional())
  @Field(type => String)
  type!: string;

  @validate(nonOptional())
  @Field(type => String)
  mediaUrl!: string;

  @Field(type => String)
  thumbnailUrl?: Maybe<string>;

  @validate(nonOptional())
  @Field(type => String)
  postUrl!: string;

  @validate(nonOptional())
  @Field(type => String)
  description!: string;
}

@Entity()
export class InstagramGalleryPagination extends AdornisEntity {
  static override _class = 'InstagramGalleryPagination';

  static defaultFields() {
    return selectionSet(() => InstagramGalleryPagination, 3);
  }

  static override resolveID(entity: InstagramGalleryPagination): string {
    if (!entity._id) return (entity._id = A.getGloballyUniqueID());
    return entity._id;
  }

  @validate(nonOptional())
  @Field(type => String)
  _id!: string;

  @validate(nonOptional())
  @Field(type => [InstagramPost])
  response!: InstagramPost[];

  @validate(nonOptional())
  @Field(type => String)
  after!: string;

  @validate(nonOptional())
  @Field(type => String)
  before!: string;

  @validate(nonOptional())
  @Field(type => String)
  next!: string;

  @Query(type => InstagramGalleryPagination)
  static fetchInstagramGalleryPaginated(
    @Arg('pageUrl', type => String) pageUrl: string,
    @Arg('limit', type => Int) limit?: number,
  ) {
    return async (
      gqlFields: BaseQLSelectionSet<InstagramGalleryPagination>,
    ): Promise<InstagramGalleryPagination | null> => {
      const token = await getInstagramToken()();
      const instagramUserID = config.get('INSTAGRAM_USER_ID');

      const reqUrl =
        pageUrl ||
        `https://graph.instagram.com/v21.0/${instagramUserID}/media?limit=${
          limit ?? 20
        }&access_token=${token}&fields=${API_RESP_ATTR_KEYS.join(',')}`;

      const apiResponse = await fetch(reqUrl, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then(response => response.json() as unknown as InstagramApiGalleryResponse)
        .then(jsonReps => getInstagramGalleryFromApiResponse(jsonReps))
        .catch(err => {
          logger.error({ err }, 'error while fetching instagram posts, Insta Offline?!');

          return null;
        });

      return apiResponse;
    };
  }
}

export enum InstagramFeedDisplayMode {
  GRID = 'Kacheln',
  MOSAIK = 'Mosaik',
  CAROUSEL = 'Karussell',
}

@Entity()
export class BaseInstagramFeed extends Base {
  static override _class = 'BaseInstagramFeed';

  @Field(type => String, { default: v => v ?? InstagramFeedDisplayMode.GRID })
  displayMode?: Maybe<InstagramFeedDisplayMode>;

  @Field(type => Int)
  countLiveFeedItems?: Maybe<number>;

  @Field(type => String)
  aspectRatio?: Maybe<string>;

  @Field(type => Int, { default: v => v ?? null })
  lengthPerRow?: Maybe<number>;

  @Field(type => String, { default: v => v ?? null })
  aspectRadio?: Maybe<string>;

  @validate(nonOptional())
  @Field(type => [InstagramPost], { default: v => v ?? [] })
  chosenPosts!: InstagramPost[];
}
